import { createSlice } from "infrastructure/store/lib";
import { sliceMixin } from "infrastructure/store/reducers";
import { MenuItem } from "consts/openBanking";
import type { OpenBankingState } from "./types";
import initialState, { sliceToken } from "./state";
import {
    getBankingSdkConnectorsThunk,
    getIbanXsBanksThunk,
    getBankingSdkRecipientsThunk,
    updateBankingSdkConnectorThunk,
    updateBankingSdkRecipientThunk,
    updateIbanXsBankThunk,
    createBankingSdkRecipientThunk,
    deleteBankingSdkRecipientThunk,
    getBankingSdkConnectorsRecipientsPivotThunk,
    getIbanXsBanksRecipientsPivotThunk,
    getIbanXsBanksCountriesPivotThunk,
    updateBankingSdkConnectorsRecipientsPivotThunk,
    deleteBankingSdkConnectorsRecipientsPivotThunk,
    updateIbanXsBanksRecipientsPivotThunk,
    deleteIbanXsBanksRecipientsPivotThunk,
    updateIbanXsBanksCountriesPivotThunk,
    deleteIbanXsBanksCountriesPivotThunk,
    createBankingSdkConnectorsRecipientsPivotThunk,
    createIbanXsBanksRecipientsPivotThunk,
    createIbanXsBanksCountriesPivotThunk
} from "./thunks";
import {
    openBankingResourceRequestLoading,
    openBankingResourceRequestRejected,
    exportOpenBankingResourceLoading,
    bankingSdkConnectorsRequestFulfilled,
    ibanXsBanksRequestFulfilled,
    bankingSdkRecipientsRequestFulfilled,
    updateOpenBankingEntityRequestFulfilled,
    createOpenBankingEntityRequestFulfilled,
    deleteOpenBankingEntityRequestFulfilled,
    bankingSdkConnectorsRecipientsPivotRequestFulfilled,
    ibanXsBanksRecipientsPivotRequestFulfilled,
    ibanXsBanksCountriesPivotRequestFulfilled,
    resetState
} from "./reducers";

const slice = createSlice({
    name: sliceToken,
    initialState,
    reducers: {
        ...sliceMixin<OpenBankingState>(initialState),
        resetBankingSdkRecipientsState: resetState(MenuItem.BankingSDKRecipients),
        changeExportBankingSdkConnectorsLoading: exportOpenBankingResourceLoading(MenuItem.BankingSDK),
        changeExportIbanXsBanksLoading: exportOpenBankingResourceLoading(MenuItem.IbanXS),
        changeExportBankingSdkRecipientsLoading: exportOpenBankingResourceLoading(MenuItem.BankingSDKRecipients),
        changeExportBankingSdkConnectorsRecipientsPivotLoading: exportOpenBankingResourceLoading(MenuItem.BankingSDKConnectorsRecipientsPivot),
        changeExportIbanXsBanksRecipientsPivotLoading: exportOpenBankingResourceLoading(MenuItem.IbanXSBanksRecipientsPivot),
        changeExportIbanXsBanksCountriesPivotLoading: exportOpenBankingResourceLoading(MenuItem.IbanXSBanksCountriesPivot)
    },
    extraReducers: builder => {
        builder
            // Get Banking SDK Connectors
            .addCase(getBankingSdkConnectorsThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDK))
            .addCase(getBankingSdkConnectorsThunk.fulfilled, bankingSdkConnectorsRequestFulfilled)
            .addCase(getBankingSdkConnectorsThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDK))
            // Updagte Banking SDK Connector
            .addCase(updateBankingSdkConnectorThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDK))
            .addCase(updateBankingSdkConnectorThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.BankingSDK))
            .addCase(updateBankingSdkConnectorThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDK))
            // Get IbanXs Banks
            .addCase(getIbanXsBanksThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXS))
            .addCase(getIbanXsBanksThunk.fulfilled, ibanXsBanksRequestFulfilled)
            .addCase(getIbanXsBanksThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXS))
            // Update IbanXs Banks
            .addCase(updateIbanXsBankThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXS))
            .addCase(updateIbanXsBankThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.IbanXS))
            .addCase(updateIbanXsBankThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXS))
            // Get Banking SDK Recipients
            .addCase(getBankingSdkRecipientsThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKRecipients))
            .addCase(getBankingSdkRecipientsThunk.fulfilled, bankingSdkRecipientsRequestFulfilled)
            .addCase(getBankingSdkRecipientsThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKRecipients))
            // Create Banking SDK Recipients
            .addCase(createBankingSdkRecipientThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKRecipients))
            .addCase(createBankingSdkRecipientThunk.fulfilled, createOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKRecipients))
            .addCase(createBankingSdkRecipientThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKRecipients))
            // Update Banking SDK Recipients
            .addCase(updateBankingSdkRecipientThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKRecipients))
            .addCase(updateBankingSdkRecipientThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKRecipients))
            .addCase(updateBankingSdkRecipientThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKRecipients))
            // Delete Banking SDK Recipients
            .addCase(deleteBankingSdkRecipientThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKRecipients))
            .addCase(deleteBankingSdkRecipientThunk.fulfilled, deleteOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKRecipients))
            .addCase(deleteBankingSdkRecipientThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKRecipients))
            // Get Banking SDK Connectors Recipients Pivot
            .addCase(getBankingSdkConnectorsRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(getBankingSdkConnectorsRecipientsPivotThunk.fulfilled, bankingSdkConnectorsRecipientsPivotRequestFulfilled)
            .addCase(getBankingSdkConnectorsRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKConnectorsRecipientsPivot))
            // Create Banking SDK Connectors Recipients Pivot
            .addCase(createBankingSdkConnectorsRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(createBankingSdkConnectorsRecipientsPivotThunk.fulfilled, createOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(createBankingSdkConnectorsRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKConnectorsRecipientsPivot))
            // Update Banking SDK Connectors Recipients Pivot
            .addCase(updateBankingSdkConnectorsRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(updateBankingSdkConnectorsRecipientsPivotThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(updateBankingSdkConnectorsRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKConnectorsRecipientsPivot))
            // Delete Banking SDK Connectors Recipients Pivot
            .addCase(deleteBankingSdkConnectorsRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(deleteBankingSdkConnectorsRecipientsPivotThunk.fulfilled, deleteOpenBankingEntityRequestFulfilled(MenuItem.BankingSDKConnectorsRecipientsPivot))
            .addCase(deleteBankingSdkConnectorsRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.BankingSDKConnectorsRecipientsPivot))
            // Get IbanXs Banks Recipients Pivot
            .addCase(getIbanXsBanksRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(getIbanXsBanksRecipientsPivotThunk.fulfilled, ibanXsBanksRecipientsPivotRequestFulfilled)
            .addCase(getIbanXsBanksRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksRecipientsPivot))
            // Create IbanXs Banks Recipients Pivot
            .addCase(createIbanXsBanksRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(createIbanXsBanksRecipientsPivotThunk.fulfilled, createOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(createIbanXsBanksRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksRecipientsPivot))
            // Update IbanXs Banks Recipients Pivot
            .addCase(updateIbanXsBanksRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(updateIbanXsBanksRecipientsPivotThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(updateIbanXsBanksRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksRecipientsPivot))
            // Delete IbanXs Banks Recipients Pivot
            .addCase(deleteIbanXsBanksRecipientsPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(deleteIbanXsBanksRecipientsPivotThunk.fulfilled, deleteOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksRecipientsPivot))
            .addCase(deleteIbanXsBanksRecipientsPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksRecipientsPivot))
            // Get IbanXs Banks Countries Pivot
            .addCase(getIbanXsBanksCountriesPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(getIbanXsBanksCountriesPivotThunk.fulfilled, ibanXsBanksCountriesPivotRequestFulfilled)
            .addCase(getIbanXsBanksCountriesPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksCountriesPivot))
            // Create IbanXs Banks Countries Pivot
            .addCase(createIbanXsBanksCountriesPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(createIbanXsBanksCountriesPivotThunk.fulfilled, createOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(createIbanXsBanksCountriesPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksCountriesPivot))
            // Update IbanXs Banks Countries Pivot
            .addCase(updateIbanXsBanksCountriesPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(updateIbanXsBanksCountriesPivotThunk.fulfilled, updateOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(updateIbanXsBanksCountriesPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksCountriesPivot))
            // Delete IbanXs Banks Countries Pivot
            .addCase(deleteIbanXsBanksCountriesPivotThunk.pending, openBankingResourceRequestLoading(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(deleteIbanXsBanksCountriesPivotThunk.fulfilled, deleteOpenBankingEntityRequestFulfilled(MenuItem.IbanXSBanksCountriesPivot))
            .addCase(deleteIbanXsBanksCountriesPivotThunk.rejected, openBankingResourceRequestRejected(MenuItem.IbanXSBanksCountriesPivot));
    }
});

export const {
    name,
    actions: {
        resetBankingSdkRecipientsState,
        changeExportBankingSdkConnectorsLoading,
        changeExportIbanXsBanksLoading,
        changeExportBankingSdkRecipientsLoading,
        changeExportBankingSdkConnectorsRecipientsPivotLoading,
        changeExportIbanXsBanksRecipientsPivotLoading,
        changeExportIbanXsBanksCountriesPivotLoading
    }
} = slice;

export default slice.reducer;
